import React, { useState, useEffect } from 'react';
import { MapPin, Bike, TreePine, Waves, Navigation, Clock, Ruler, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { trailsAPI } from '../utils/api';

// Fallback data in case API is empty
const fallbackTrails = [
  {
    id: "fallback-1",
    name: "Cornwall Waterfront Trail",
    distance: "12 km",
    difficulty: "Easy",
    duration: "2-3 hours",
    type: "Paved Multi-use",
    highlights: "St. Lawrence River panoramic views, Lamoureux Park, Cornwall Marina, picnic pavilions",
    description: "The iconic Cornwall Waterfront Trail stretches along the stunning St. Lawrence River, offering uninterrupted views of the water and the Adirondack Mountains of New York State.",
    amenities: ["Free parking", "Public washrooms", "Water fountains", "Benches", "Bike racks", "Beach access"],
    coordinates: { lat: 45.0215, lng: -74.7302 },
    startPoint: "Lamoureux Park, 100 Water St E, Cornwall",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800"
  },
  {
    id: "fallback-2",
    name: "Guindon Park Conservation Area",
    distance: "8 km (network)",
    difficulty: "Easy to Moderate",
    duration: "1-3 hours",
    type: "Natural Surface & Paved",
    highlights: "Old-growth forest, diverse bird species, scenic lookouts, cross-country ski trails",
    description: "Guindon Park offers a network of trails through beautiful natural forest along the St. Lawrence River.",
    amenities: ["Large parking lot", "Trail maps at entrance", "Picnic areas", "Washrooms", "Dog-friendly (on leash)"],
    coordinates: { lat: 45.0089, lng: -74.7821 },
    startPoint: "Guindon Park, County Rd 2, Cornwall",
    image: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800"
  },
  {
    id: "fallback-3",
    name: "Lamoureux Park Loop",
    distance: "3.5 km",
    difficulty: "Easy",
    duration: "45 minutes",
    type: "Paved",
    highlights: "Gardens, splash pad, Cornwall Community Museum, band shell, river beach",
    description: "A beautiful urban park loop perfect for families with manicured gardens and beach access.",
    amenities: ["Free parking", "Washrooms", "Playground", "Splash pad", "Beach"],
    coordinates: { lat: 45.0192, lng: -74.7334 },
    startPoint: "Lamoureux Park, 100 Water St E, Cornwall",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800"
  }
];

export const TrailMapsPage = () => {
  const [expandedTrail, setExpandedTrail] = useState(null);
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrails = async () => {
      try {
        const response = await trailsAPI.getAll();
        if (response.data && response.data.length > 0) {
          setTrails(response.data);
        } else {
          setTrails(fallbackTrails);
        }
      } catch (error) {
        console.error('Error fetching trails:', error);
        setTrails(fallbackTrails);
      } finally {
        setLoading(false);
      }
    };
    fetchTrails();
  }, []);

  const getDifficultyColor = (difficulty) => {
    if (difficulty.includes('Easy') && !difficulty.includes('Moderate')) return 'bg-green-100 text-green-700 border-green-300';
    if (difficulty.includes('Moderate') && !difficulty.includes('Difficult')) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (difficulty.includes('Difficult')) return 'bg-red-100 text-red-700 border-red-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  const getTypeIcon = (type) => {
    if (type.toLowerCase().includes('natural')) return <TreePine className="w-4 h-4" />;
    if (type.toLowerCase().includes('water')) return <Waves className="w-4 h-4" />;
    return <Bike className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <section className="pt-24 pb-20 bg-gradient-to-br from-emerald-50 to-blue-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="ml-3 text-slate-600">Loading trails...</span>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-emerald-50 to-blue-50 min-h-screen" data-testid="trail-maps-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full mb-4">
            <MapPin className="w-5 h-5 text-emerald-700" />
            <span className="text-sm font-semibold text-emerald-700">Cornwall Trails</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Explore Our Trails
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Discover over 45 kilometers of scenic trails along the St. Lawrence River, through conservation areas, and across beautiful parks.
          </p>
        </div>

        {/* Trail Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Card className="bg-white/80 backdrop-blur border-emerald-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-1">45+</div>
              <div className="text-sm text-slate-600">Kilometers of Trails</div>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur border-emerald-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-1">{trails.length}</div>
              <div className="text-sm text-slate-600">Trail Systems</div>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur border-emerald-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-1">Free</div>
              <div className="text-sm text-slate-600">Access Year-Round</div>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur border-emerald-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-1">All</div>
              <div className="text-sm text-slate-600">Skill Levels Welcome</div>
            </CardContent>
          </Card>
        </div>

        {/* Interactive Map */}
        <Card className="mb-12 overflow-hidden border-2 border-emerald-200">
          <CardHeader className="bg-emerald-50">
            <CardTitle className="text-xl flex items-center gap-2">
              <Navigation className="w-5 h-5 text-emerald-600" />
              Trail Map - Cornwall Area
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="aspect-video">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44898.45723929787!2d-74.78!3d45.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ccc17e93c8f8d6d%3A0x5040cadae4d8620!2sCornwall%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cornwall Trails Map"
              />
            </div>
          </CardContent>
        </Card>

        {/* Trail Cards */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Detailed Trail Information</h2>
          
          {trails.map((trail) => (
            <Card 
              key={trail.id} 
              className="overflow-hidden border-2 border-emerald-200 hover:border-emerald-400 transition-colors"
              data-testid={`trail-card-${trail.id}`}
            >
              <div className="md:flex">
                {/* Trail Image */}
                <div className="md:w-1/3 h-48 md:h-auto relative">
                  <img 
                    src={trail.image} 
                    alt={trail.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className={`${getDifficultyColor(trail.difficulty)} border`}>
                      {trail.difficulty}
                    </Badge>
                  </div>
                </div>

                {/* Trail Info */}
                <div className="md:w-2/3 p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold text-slate-900">{trail.name}</h3>
                    <div className="flex items-center gap-1 text-emerald-600">
                      {getTypeIcon(trail.type)}
                      <span className="text-sm">{trail.type}</span>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center gap-1 text-slate-600">
                      <Ruler className="w-4 h-4" />
                      <span className="text-sm">{trail.distance}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{trail.duration}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{trail.startPoint?.split(',')[0] || 'Cornwall'}</span>
                    </div>
                  </div>

                  {/* Highlights */}
                  <p className="text-slate-600 text-sm mb-3">
                    <strong>Highlights:</strong> {trail.highlights}
                  </p>

                  {/* Description Preview */}
                  <p className="text-slate-600 mb-4 line-clamp-2">{trail.description}</p>

                  {/* Expand Button */}
                  <Button
                    variant="outline"
                    onClick={() => setExpandedTrail(expandedTrail === trail.id ? null : trail.id)}
                    className="w-full justify-between"
                  >
                    <span>{expandedTrail === trail.id ? 'Show Less' : 'View Full Details'}</span>
                    {expandedTrail === trail.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>

                  {/* Expanded Content */}
                  {expandedTrail === trail.id && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Full Description</h4>
                        <p className="text-slate-600">{trail.description}</p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Amenities</h4>
                        <div className="flex flex-wrap gap-2">
                          {trail.amenities?.map((amenity, idx) => (
                            <Badge key={idx} variant="outline" className="bg-emerald-50">
                              {amenity}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2">Getting There</h4>
                        <p className="text-slate-600 mb-2">{trail.startPoint}</p>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${trail.coordinates?.lat},${trail.coordinates?.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
                        >
                          <Navigation className="w-4 h-4" />
                          Get Directions
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Trail Tips */}
        <Card className="mt-12 border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <TreePine className="w-5 h-5 text-blue-600" />
              Trail Tips & Safety
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Before You Go</h4>
                <ul className="text-slate-600 space-y-1 text-sm">
                  <li>• Check weather conditions before heading out</li>
                  <li>• Bring water and snacks for longer trails</li>
                  <li>• Wear appropriate footwear for the trail surface</li>
                  <li>• Tell someone your planned route and return time</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Trail Etiquette</h4>
                <ul className="text-slate-600 space-y-1 text-sm">
                  <li>• Stay on marked trails to protect vegetation</li>
                  <li>• Pack out all garbage - leave no trace</li>
                  <li>• Keep dogs on leash where required</li>
                  <li>• Yield to other trail users appropriately</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default TrailMapsPage;
